import { useSlideStore } from "@/features/tutor/store/useSlideStore";
import { useViewport } from "@/hooks/useViewport";
import ASSESSMENT_TYPES from "@/features/tutor/constants/assessmentTypes";

const IconCircle = ({ src, bg, padding, small = false }) => (
  <div
    className="flex items-center justify-center shrink-0"
    style={{
      width: small ? 100 : 172,
      height: small ? 100 : 172,
      borderRadius: 500,
      background: bg,
      padding,
    }}
  >
    <img
      src={src}
      alt=""
      style={{ width: "100%", height: "100%", objectFit: "contain" }}
    />
  </div>
);

const LessonBadge = ({ name, colors }) => (
  <span
    className="inline-flex items-center justify-center"
    style={{
      backgroundColor: colors.bg,
      color: colors.text,
      borderRadius: 20,
      gap: 10,
      padding: "12px 20px",
      fontFamily: "Nunito, sans-serif",
      fontWeight: 700,
      fontSize: 13,
      lineHeight: "100%",
      letterSpacing: "0.2px",
    }}
  >
    {name}
  </span>
);

// ─── Portrait layout (default) ────────────────────────────────────────────────

function PortraitLayout({ modal, currentLessonName, onStart, onSkip }) {
  return (
    <div className="flex flex-col items-center text-center px-8 py-10 gap-5 w-full">
      {modal.icon && (
        <IconCircle
          src={modal.icon}
          bg={modal.iconBg}
          padding={modal.iconPadding}
        />
      )}

      <h2
        className="text-[#252525]"
        style={{
          fontFamily: "Nunito, sans-serif",
          fontWeight: 700,
          fontSize: 26,
          lineHeight: "120%",
          letterSpacing: "0.2px",
        }}
      >
        {modal.title}
      </h2>

      {currentLessonName && modal.showLessonBadge !== false && (
        <LessonBadge name={currentLessonName} colors={modal.badgeColors} />
      )}

      <p
        className="text-center whitespace-pre-line"
        style={{
          color: modal.bodyColor || "#6B7280",
          fontFamily: "Mulish, sans-serif",
          fontWeight: 600,
          fontSize: 17,
          lineHeight: "140%",
          letterSpacing: "0.2px",
        }}
      >
        {modal.body}
      </p>

      <div className="flex flex-col items-center gap-3 w-full mt-1">
        <button
          onClick={onStart}
          className="text-white transition-colors w-full max-w-[280px]"
          style={{
            backgroundColor: modal.accentColor,
            padding: "16px 32px",
            whiteSpace: "nowrap",
            borderRadius: 10,
            fontFamily: "Nunito, sans-serif",
            fontWeight: 700,
            fontSize: 18,
            lineHeight: "100%",
            letterSpacing: "0.2px",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.backgroundColor = modal.accentHover;
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.backgroundColor = modal.accentColor;
          }}
        >
          {modal.startButton}
        </button>
        {onSkip && (
          <button
            onClick={onSkip}
            className="transition-colors"
            style={{
              color: modal.accentColor,
              fontFamily: "Nunito, sans-serif",
              fontWeight: 700,
              fontSize: 14,
              lineHeight: "100%",
              letterSpacing: "0.2px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.color = modal.accentHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.color = modal.accentColor;
            }}
          >
            {modal.skipButton}
          </button>
        )}
      </div>
    </div>
  );
}

// ─── Landscape layout ─────────────────────────────────────────────────────────

function LandscapeLayout({ modal, currentLessonName, onStart, onSkip }) {
  return (
    <div className="flex flex-row items-center w-full px-6 py-5 gap-6">
      {/* Left: icon (only if present) */}
      {modal.icon && (
        <div className="shrink-0 flex items-center justify-center">
          <IconCircle
            src={modal.icon}
            bg={modal.iconBg}
            padding={modal.iconPadding}
            small
          />
        </div>
      )}

      {/* Right: text + buttons */}
      <div className="flex flex-col items-center text-center gap-3 flex-1 min-w-0">
        <h2
          className="text-[#252525]"
          style={{
            fontFamily: "Nunito, sans-serif",
            fontWeight: 700,
            fontSize: 20,
            lineHeight: "120%",
            letterSpacing: "0.2px",
          }}
        >
          {modal.title}
        </h2>

        {currentLessonName && modal.showLessonBadge !== false && (
          <LessonBadge name={currentLessonName} colors={modal.badgeColors} />
        )}

        <p
          className="text-center whitespace-pre-line"
          style={{
            color: modal.bodyColor || "#6B7280",
            fontFamily: "Mulish, sans-serif",
            fontWeight: 600,
            fontSize: 14,
            lineHeight: "140%",
            letterSpacing: "0.2px",
          }}
        >
          {modal.body}
        </p>

        <div className="flex flex-col items-center gap-2 w-full">
          <button
            onClick={onStart}
            className="text-white transition-colors w-full max-w-[240px]"
            style={{
              backgroundColor: modal.accentColor,
              padding: "12px 24px",
              whiteSpace: "nowrap",
              borderRadius: 10,
              fontFamily: "Nunito, sans-serif",
              fontWeight: 700,
              fontSize: 15,
              lineHeight: "100%",
              letterSpacing: "0.2px",
            }}
            onMouseEnter={(e) => {
              e.currentTarget.style.backgroundColor = modal.accentHover;
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.backgroundColor = modal.accentColor;
            }}
          >
            {modal.startButton}
          </button>
          {onSkip && (
            <button
              onClick={onSkip}
              className="transition-colors"
              style={{
                color: modal.accentColor,
                fontFamily: "Nunito, sans-serif",
                fontWeight: 700,
                fontSize: 13,
                lineHeight: "100%",
                letterSpacing: "0.2px",
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.color = modal.accentHover;
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.color = modal.accentColor;
              }}
            >
              {modal.skipButton}
            </button>
          )}
        </div>
      </div>
    </div>
  );
}

// ─── Main component ───────────────────────────────────────────────────────────

const AssessmentModal = ({
  type,
  onStart,
  onSkip,
  lessonName: lessonNameProp,
}) => {
  const config = ASSESSMENT_TYPES[type];
  const storeLesonName = useSlideStore((s) => s.currentLessonName);
  const { isMobileLandscape } = useViewport();
  if (!config) return null;

  const { modal } = config;
  const currentLessonName = lessonNameProp || storeLesonName;

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center p-4">
      <div
        className="absolute inset-0 bg-black/50 backdrop-blur-sm"
        aria-hidden="true"
      />
      <div
        role="dialog"
        aria-modal="true"
        className="relative bg-white shadow-xl z-10 rounded-[20px] overflow-hidden max-h-[90dvh] overflow-y-auto"
        style={{
          width: isMobileLandscape ? "min(560px, 95vw)" : modal.width || 558,
          maxWidth: "95vw",
        }}
      >
        {isMobileLandscape ? (
          <LandscapeLayout
            modal={modal}
            currentLessonName={currentLessonName}
            onStart={onStart}
            onSkip={onSkip}
          />
        ) : (
          <PortraitLayout
            modal={modal}
            currentLessonName={currentLessonName}
            onStart={onStart}
            onSkip={onSkip}
          />
        )}
      </div>
    </div>
  );
};

export default AssessmentModal;
